import { useState, useRef, useEffect } from 'react';

// Animated Counter Component
export const AnimatedCounter = ({ value, suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            // Skip animation for "24/7" type values
            if (value.includes('/')) {
              setCount(parseFloat(value.replace(/[^0-9.]/g, '')));
              return;
            }
            
            const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
            
            const startTime = Date.now();
            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Easing function for smooth animation
              const easeOutQuart = 1 - Math.pow(1 - progress, 4);
              const current = numericValue * easeOutQuart;
              
              setCount(current);
              
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                setCount(numericValue);
              }
            };
            
            animate();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [value, duration, hasAnimated]);

  const displayValue = () => {
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
    const isPercentage = value.includes('%');
    const hasPlus = value.includes('+');
    const hasX = value.includes('x');
    const hasLessThan = value.includes('<');
    const hasS = value.includes('s');
    const hasSlash = value.includes('/');
    
    if (hasLessThan && hasS) {
      // Handle "<2s" case
      return '<' + Math.round(count) + 's';
    } else if (hasSlash) {
      // Handle "24/7" case - don't animate
      return value;
    } else if (isPercentage) {
      return Math.round(count) + '%';
    } else if (hasPlus) {
      return Math.round(count) + '+';
    } else if (hasX) {
      return Math.round(count) + 'x';
    } else {
      return Math.round(count);
    }
  };

  return <span ref={counterRef}>{displayValue()}</span>;
};

